// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { CHECKOUT_SHIPPING_FORM_ID } from "@rpsc/Overrides/Widgets/OnePageCheckout/OnePageCheckoutShippingSection";
import { xResetValidationSubmit, xValidateOnSubmit } from "@rpsc/XDL/Utilities";
import { useMemo, useState } from "react";

interface Props {
    setUseOneTimeAddress: (
        parameter: {
            useOneTimeAddress: boolean;
        } & HasOnComplete<{
            cart: Cart;
            shipTosForBillTo: ShipToModel[];
            shipToForEditing: ShipToModel;
        }>,
    ) => void;
    setIsBillingAddressTheSameAsShipping?: (props: any) => void;
    resetShippingAddressFormValidation?: () => void;
}

const useCheckoutOneTimeAddress = ({
    setUseOneTimeAddress,
    setIsBillingAddressTheSameAsShipping,
    resetShippingAddressFormValidation,
}: Props) => {
    const [selectedCustomShipToAddress, setSelectedCustomShipToAddress] = useState<ShipToModel | undefined>();
    const { cart, shippingAddressFormState, useOneTimeAddress, shipToState, shipToAddressFields } = useAppSelector(
        (state: ApplicationState) => {
            const cart = getCurrentCartState(state).value;
            return {
                cart,
                shippingAddressFormState: state.pages.onePageCheckout.shippingAddressFormState,
                useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
                shipToState: getShipToState(state, cart ? cart.shipToId : undefined),
                shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
                isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
            };
        },
    );

    const toggleOneTimeAddress = (isOneTimeAddress: boolean, useBillingAddress = false) => {
        setIsBillingAddressTheSameAsShipping?.({
            isBillingAddressTheSameAsShipping: !isOneTimeAddress && useBillingAddress,
        });
        setUseOneTimeAddress({
            useOneTimeAddress: isOneTimeAddress,
            onComplete: () => {
                xResetValidationSubmit(CHECKOUT_SHIPPING_FORM_ID);
                resetShippingAddressFormValidation?.();
            },
        });
    };

    return {
        shippingAddressFormState,
        useOneTimeAddress,
        selectedCustomShipToAddress,
        setSelectedCustomShipToAddress,
        toggleOneTimeAddress,
        shipToAddressFields,
    };
};

export default useCheckoutOneTimeAddress;
